import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Scale, X, ArrowRight } from 'lucide-react';
import { KLACHTENREGLEMENTEN, reglementVariantVoorRol, type ReglementVariant } from './klachtenreglementen';
import { useAuthStore } from '../../store/authStore';

/** Plek van het klachtenformulier per rol (de vier vaste ingangen + beheer). */
function klachtFormulierPad(role?: string | null): string | null {
  switch (role) {
    case 'opdrachtgever':
    case 'employer_member':
      return '/employer/mijn-belagom?tab=juridisch';
    case 'professional':
      return '/professional/belagom?view=contracten';
    case 'opleider':
      return '/opleider/finance/helpdesk';
    case 'medewerker':
      return '/medewerker/academy?helpdesk=1';
    case 'admin':
      return '/admin/klachten';
    default:
      return null;
  }
}

/**
 * Het volledige klachtenreglement in een pop-up, met PDF-download en (optioneel) de doorlink naar het formulier.
 * Medewerkers zien de consumentenversie, zakelijke rollen de B2B-versie; beheer kan tussen beide wisselen.
 */
export const KlachtenreglementModal: React.FC<{ open: boolean; onClose: () => void; toonFormulierLink?: boolean; variant?: ReglementVariant }> = ({
  open,
  onClose,
  toonFormulierLink = true,
  variant,
}) => {
  const { t } = useTranslation('klachten');
  const role = useAuthStore((s) => s.user?.role);
  const formulierPad = toonFormulierLink ? klachtFormulierPad(role) : null;
  const [keuze, setKeuze] = useState<ReglementVariant | null>(null);
  const actief = keuze ?? variant ?? reglementVariantVoorRol(role);
  const reglement = KLACHTENREGLEMENTEN[actief];

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="klachtenreglement-titel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-navy-900" aria-hidden="true" />
            <h2 id="klachtenreglement-titel" className="text-base font-semibold text-gray-900">{actief === 'consument' ? t('consument.modalTitel') : t('modal.titel')}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-500 hover:bg-gray-100" aria-label={t('knop.sluiten')}>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4 text-sm text-gray-700">
          {role === 'admin' && (
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5" role="group" aria-label={t('consument.wissel')}>
              {(['zakelijk', 'consument'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setKeuze(v)}
                  aria-pressed={actief === v}
                  className={`rounded-md px-3 py-1 text-xs font-medium ${actief === v ? 'bg-navy-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {t(`consument.variant.${v}`)}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <a href={reglement.pdf} download className="inline-flex items-center gap-2 rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
              <Download className="h-4 w-4" aria-hidden="true" />
              {t('kop.pdf')}
            </a>
            {formulierPad && (
              <Link to={formulierPad} onClick={onClose} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                {t('knop.naarFormulier')}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </div>

          <p>{reglement.toepassing}</p>

          <section>
            <h3 className="mb-2 font-semibold text-gray-900">{t('modal.procedure')}</h3>
            <p className="mb-2">{reglement.procedure.inleiding}</p>
            <ol className="space-y-2">
              {reglement.procedure.stappen.map((stap, i) => (
                <li key={stap.titel} className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-navy-50 text-xs font-semibold text-navy-900">{i + 1}</span>
                  <span>
                    <strong className="text-gray-900">{stap.titel}.</strong> {stap.tekst}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          <section>
            <h3 className="mb-2 font-semibold text-gray-900">{t('modal.reglement')}</h3>
            <div className="space-y-4">
              {reglement.artikelen.map((artikel) => (
                <div key={artikel.nummer}>
                  <p className="font-semibold text-navy-900">
                    {t('modal.artikel', { nummer: artikel.nummer })} — {artikel.titel}
                  </p>
                  <ol className="mt-1 space-y-1">
                    {artikel.leden.map((lid, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="w-8 flex-shrink-0 font-medium text-gray-500">
                          {artikel.nummer}.{i + 1}
                        </span>
                        <span>{lid}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-gray-400">Versie {reglement.versie}</p>
          </section>
        </div>
      </div>
    </div>
  );
};

/** Tekstlink voor de footer van alle ingelogde omgevingen. */
export const KlachtenreglementFooterLink: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation('klachten');
  const consument = useAuthStore((s) => reglementVariantVoorRol(s.user?.role) === 'consument');
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className ?? 'inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-navy-900 hover:underline'}>
        <Scale className="h-3.5 w-3.5" aria-hidden="true" />
        {consument ? t('consument.footer') : t('footer.link')}
      </button>
      <KlachtenreglementModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};
